import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Home, Building2, MapPin, ArrowRight, Info, X } from "lucide-react";

import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { Section, CTABand } from "@/components/site/Blocks";
import { publishedInvestorCities, type InvestorCity } from "@/data/investor-cities";
import { buildHead } from "@/lib/seo-head";
import { getFmrByZip, type FmrResult } from "@/lib/fmr.functions";
import heroCompareCities from "@/assets/hero/hero-compare-cities-fmr-airbnb.jpg";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

type CompareSearch = { state?: string; cities?: string };

export const Route = createFileRoute("/tools/compare-cities")({
  validateSearch: (search: Record<string, unknown>): CompareSearch => ({
    // Optional so the bare URL loads directly instead of redirecting to ?state=&cities=.
    state: typeof search.state === "string" && search.state ? search.state : undefined,
    cities: typeof search.cities === "string" && search.cities ? search.cities : undefined,
  }),
  head: () =>
    buildHead({
      title: "Compare Cities — FMR vs Airbnb Income Tool | Simply Approved",
      description:
        "Compare two or more investor cities side by side. See HUD Section 8 Fair Market Rent and modeled Airbnb income for each city in the same state.",
      url: "/tools/compare-cities",
      keywords:
        "compare investor cities tool, FMR vs Airbnb income, Section 8 vs short term rental comparison, compare rental markets, city rental income comparator, HUD FMR city comparison, Airbnb income by city, best rental market comparison tool, Section 8 rent vs STR income, investor city comparison calculator",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "Compare Cities", url: "/tools/compare-cities" },
      ],
      service: { name: "Compare Cities Investment Tool" },
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "Compare Cities — FMR vs Airbnb Income",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/tools/compare-cities",
          description:
            "Compare HUD Section 8 Fair Market Rent and modeled Airbnb income across investor cities in the same state.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: CompareCitiesPage,
});

function parseMoney(s?: string): number {
  if (!s) return 0;
  const n = parseInt(s.replace(/[^0-9]/g, ""), 10);
  return Number.isFinite(n) ? n : 0;
}
const fmt = (n: number) => (n > 0 ? `$${n.toLocaleString()}` : "—");
const pct = (n: number) => (n > 0 ? `${n.toFixed(1)}%` : "—");

function airbnbModel(city: InvestorCity, fmr2BR: number) {
  const base = parseMoney(city.medianRent2BR) || fmr2BR;
  const adr = city.airbnbAdr ?? Math.round(base / 10) * 3;
  const occLow = city.airbnbOccupancy ?? 0.55;
  const occHigh = city.airbnbOccupancy ? Math.min(0.85, city.airbnbOccupancy + 0.15) : 0.72;
  const occMid = (occLow + occHigh) / 2;
  const monthlyLow = base > 0 ? Math.round(adr * 30 * occLow) : 0;
  const monthlyHigh = base > 0 ? Math.round(adr * 30 * occHigh) : 0;
  const monthlyMid = base > 0 ? Math.round(adr * 30 * occMid) : 0;
  return { adr, occLow, occHigh, occMid, monthlyLow, monthlyHigh, monthlyMid };
}

function CompareCitiesPage() {
  const navigate = Route.useNavigate();
  const { state = "", cities = "" } = Route.useSearch();

  // Group cities by state
  const statesWithCities = useMemo(() => {
    const map = new Map<string, { slug: string; name: string; cities: InvestorCity[] }>();
    for (const c of publishedInvestorCities) {
      if (!map.has(c.stateSlug)) {
        map.set(c.stateSlug, { slug: c.stateSlug, name: c.stateName, cities: [] });
      }
      map.get(c.stateSlug)!.cities.push(c);
    }
    return Array.from(map.values())
      .filter((s: { cities: InvestorCity[] }) => s.cities.length >= 2)
      .sort((a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name));
  }, []);

  const activeState = statesWithCities.find((s: { slug: string }) => s.slug === state) ?? null;
  const availableCities: InvestorCity[] = activeState?.cities ?? [];
  const selectedSlugs = cities ? cities.split(",").filter(Boolean) : [];
  const selected: InvestorCity[] = selectedSlugs
    .map((slug: string) => availableCities.find((c: InvestorCity) => c.slug === slug))
    .filter((c: InvestorCity | undefined): c is InvestorCity => Boolean(c));

  const setState = (slug: string) => {
    navigate({ search: { state: slug, cities: "" }, replace: true });
  };
  const toggleCity = (slug: string) => {
    const next = selectedSlugs.includes(slug)
      ? selectedSlugs.filter((s: string) => s !== slug)
      : [...selectedSlugs, slug];
    navigate({ search: { state, cities: next.join(",") }, replace: true });
  };
  const clearCities = () => navigate({ search: { state, cities: "" }, replace: true });

  const fmrFn = useServerFn(getFmrByZip);
  const fmrQueries = useQueries({
    queries: selected.map((c: InvestorCity) => ({
      queryKey: ["fmr-zip", c.centroidZip],
      queryFn: () => fmrFn({ data: { zip: c.centroidZip } }),
      staleTime: 1000 * 60 * 60 * 24,
      retry: 1,
    })),
  });

  const rows = selected.map((city, i) => {
    const q = fmrQueries[i];
    const fmr = q?.data as FmrResult | undefined;
    const s2 = fmr?.rents.twoBr ?? 0;
    const abb = airbnbModel(city, s2);
    const homeVal = parseMoney(city.medianHomeValue);
    const s8Yield = homeVal > 0 && s2 > 0 ? ((s2 * 12) / homeVal) * 100 : 0;
    const strYield = homeVal > 0 && abb.monthlyMid > 0 ? ((abb.monthlyMid * 12) / homeVal) * 100 : 0;
    return {
      city,
      loading: q?.isLoading ?? false,
      error: q?.isError ?? false,
      fmr,
      s2,
      s8Yield,
      abb,
      strYield,
      homeVal,
    };
  });

  const canCompare = selected.length >= 2;

  return (
    <SiteShell>
      <PageHero
        eyebrow="Free Investor Tool"
        title="Compare Cities — FMR vs Airbnb"
        subtitle="Pick a state, choose two or more cities, and see HUD Section 8 Fair Market Rent side-by-side with modeled Airbnb income."
        image={{ src: heroCompareCities, alt: "Investor couple comparing two cities' rental income on a tablet outside a rental home, with a vacation cottage nearby" }}
      />
      <PageEssentials />

      <Section>
        {/* Step 1: State */}
        <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
          <div className="mb-4">
            <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
              Step 1
            </div>
            <h2 className="mt-1 font-display text-xl font-bold text-brand-dark">
              Select a state
            </h2>
            <p className="text-sm text-muted-foreground">
              We compare cities within the same state so market context stays consistent.
            </p>
          </div>
          <label className="block">
            <span className="sr-only">State</span>
            <select
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm"
            >
              <option value="">— Choose a state —</option>
              {statesWithCities.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name} ({s.cities.length} cities)
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Step 2: Cities */}
        {activeState && (
          <div className="mt-6 rounded-2xl border border-border bg-card p-6 md:p-8">
            <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-widest text-brand-gold">
                  Step 2
                </div>
                <h2 className="mt-1 font-display text-xl font-bold text-brand-dark">
                  Choose cities in {activeState.name}
                </h2>
                <p className="text-sm text-muted-foreground">
                  Select at least 2. Selected: <strong>{selected.length}</strong>
                </p>
              </div>
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={clearCities}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:bg-muted"
                >
                  <X className="h-3.5 w-3.5" /> Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {availableCities.map((c) => {
                const on = selectedSlugs.includes(c.slug);
                return (
                  <button
                    key={c.slug}
                    type="button"
                    onClick={() => toggleCity(c.slug)}
                    aria-pressed={on}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      on
                        ? "border-brand-gold bg-brand-gold text-brand-dark shadow"
                        : "border-border bg-background text-foreground hover:border-brand-gold/60"
                    }`}
                  >
                    <MapPin className="h-3.5 w-3.5" /> {c.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Results */}
        {canCompare && (
          <div className="mt-6 space-y-6">
            {/* City cards grid */}
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {rows.map((r) => (
                <article
                  key={r.city.slug}
                  className="overflow-hidden rounded-2xl border border-border bg-card"
                >
                  <header className="border-b border-border bg-gradient-to-r from-brand-dark to-brand px-5 py-4 text-primary-foreground">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-brand-gold">
                      {r.city.stateName}
                    </div>
                    <div className="font-display text-lg font-bold">{r.city.name}</div>
                    <div className="text-xs text-primary-foreground/80">
                      ZIP {r.city.centroidZip} · Median home {r.city.medianHomeValue}
                    </div>
                  </header>
                  <div className="grid grid-cols-2 gap-px bg-border">
                    <div className="bg-card p-4">
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Home className="h-3.5 w-3.5" /> Section 8 · 2BR FMR
                      </div>
                      <div className="mt-1 font-display text-2xl font-bold text-brand-dark">
                        {r.loading ? "…" : fmt(r.s2)}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        Annual {fmt(r.s2 * 12)} · Yield {pct(r.s8Yield)}
                      </div>
                    </div>
                    <div className="bg-card p-4">
                      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                        <Building2 className="h-3.5 w-3.5" /> Airbnb (mid)
                      </div>
                      <div className="mt-1 font-display text-2xl font-bold text-brand-dark">
                        {fmt(r.abb.monthlyMid)}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        Range {fmt(r.abb.monthlyLow)}–{fmt(r.abb.monthlyHigh)} · Yield {pct(r.strYield)}
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-border px-5 py-3 text-xs text-muted-foreground">
                    ADR ~{fmt(r.abb.adr)} · Occ {Math.round(r.abb.occLow * 100)}–{Math.round(r.abb.occHigh * 100)}%
                  </div>
                  <div className="flex items-center justify-between border-t border-border bg-muted/40 px-5 py-3 text-xs">
                    <Link
                      to="/dscr-loans/$state/$city"
                      params={{ state: r.city.stateSlug, city: r.city.slug }}
                      className="font-semibold text-brand hover:underline"
                    >
                      City guide →
                    </Link>
                    <a
                      href={`/apply?source=compare-${r.city.slug}`}
                      className="inline-flex items-center gap-1 rounded-md bg-brand-gold px-3 py-1.5 font-semibold text-primary hover:bg-brand-gold-hover"
                    >
                      Price it <ArrowRight className="h-3 w-3" />
                    </a>
                  </div>
                </article>
              ))}
            </div>

            {/* Side-by-side table */}
            <div className="overflow-hidden rounded-2xl border border-border bg-card">
              <header className="border-b border-border bg-muted/40 px-6 py-4">
                <h2 className="font-display text-lg font-bold text-brand-dark">
                  Side-by-side comparison
                </h2>
                <p className="text-xs text-muted-foreground">
                  Section 8 = live HUD 2BR FMR. Airbnb = modeled from local 2BR benchmark
                  (ADR ≈ 2BR × 0.3, occupancy 55–72%). Illustrative only.
                </p>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold">Metric</th>
                      {rows.map((r) => (
                        <th key={r.city.slug} className="px-3 py-2 text-right font-semibold text-brand-dark">
                          {r.city.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <TRow label="Median home value" values={rows.map((r) => fmt(r.homeVal))} />
                    <TRow label="Section 8 · 2BR FMR (monthly)" values={rows.map((r) => (r.loading ? "…" : fmt(r.s2)))} strong />
                    <TRow label="Section 8 · annual gross" values={rows.map((r) => fmt(r.s2 * 12))} />
                    <TRow label="Section 8 · gross yield" values={rows.map((r) => pct(r.s8Yield))} />
                    <TRow label="Airbnb · monthly (low)" values={rows.map((r) => fmt(r.abb.monthlyLow))} />
                    <TRow label="Airbnb · monthly (mid)" values={rows.map((r) => fmt(r.abb.monthlyMid))} strong />
                    <TRow label="Airbnb · monthly (high)" values={rows.map((r) => fmt(r.abb.monthlyHigh))} />
                    <TRow label="Airbnb · annual (mid)" values={rows.map((r) => fmt(r.abb.monthlyMid * 12))} />
                    <TRow label="Airbnb · gross yield (mid)" values={rows.map((r) => pct(r.strYield))} />
                    <TRow label="Airbnb ADR (modeled)" values={rows.map((r) => fmt(r.abb.adr))} />
                    <TRow
                      label="STR vs Section 8 (monthly Δ)"
                      values={rows.map((r) => {
                        const delta = r.abb.monthlyMid - r.s2;
                        if (!r.s2 || !r.abb.monthlyMid) return "—";
                        const sign = delta >= 0 ? "+" : "−";
                        return `${sign}$${Math.abs(delta).toLocaleString()}`;
                      })}
                    />
                  </tbody>
                </table>
              </div>
              <p className="flex items-start gap-2 border-t border-border bg-muted/30 px-6 py-3 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Airbnb figures are illustrative and do not include opex, cleaning, management, or
                local STR regulations. Section 8 requires PHA inspections. Verify with AirDNA/Rabbu
                and local ordinances before underwriting.
              </p>
            </div>
          </div>
        )}

        {activeState && !canCompare && (
          <div className="mt-6 rounded-xl border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
            Select at least 2 cities in {activeState.name} to compare.
          </div>
        )}

        {!activeState && (
          <div className="mt-6 rounded-xl border border-dashed border-border bg-muted/30 p-6 text-center text-sm text-muted-foreground">
            Choose a state above to get started.
          </div>
        )}
      </Section>

      <CTABand
        title="Ready to price a deal?"
        body="We DSCR-qualify to Section 8 HAP rent, long-term market rent, or 12-month STR revenue."
        cta="Get Your Personalized Rate Quote"
        href="/apply"
      />
    <PageOutro />
      </SiteShell>
  );
}

function TRow({ label, values, strong }: { label: string; values: string[]; strong?: boolean }) {
  return (
    <tr>
      <td className="px-3 py-2 text-muted-foreground">{label}</td>
      {values.map((v, i) => (
        <td
          key={i}
          className={`px-3 py-2 text-right ${strong ? "font-display font-bold text-brand-dark" : "text-foreground"}`}
        >
          {v}
        </td>
      ))}
    </tr>
  );
}
